import { useCallback, useEffect, useMemo, useState, type MutableRefObject, type RefObject } from 'react';
import { useCursorStatementEditInfo } from '../../visual-query-editor/compact/useCursorStatementEditInfo';
import { useParseErrorMarker } from '../../visual-query-editor/compact/useParseErrorMarker';
import { resolveLiveQuery } from '../components/liveQuery';
import { databaseEngineIdToSqlEngine } from '../../visual-query-editor/edit/livePullSchemaSnapshot';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { CompactVqeSource } from '../../visual-query-editor/compact/CompactVqePopover';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import { useBetaFeature } from '../../../../core/beta';

type UseCompactEditAffordanceParams = {
  engineId: DatabaseEngineId;
  /** Debounced React query state (classification fallback when the editor is not mounted). */
  query: string;
  isActive: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  /** `affordanceStatement?.range ?? null` from `useSyntaxErrorSignal`. */
  affordanceStatementRange: EditorSelectionRange | null;
  hasMarkerSyntaxError: boolean;
  hasLocalSyntaxError: boolean;
};

/**
 * Minimal visual-edit affordance (WS2). The pencil follows the same
 * `showInlineExecuteBar` setting as Execute/Peek: gutter icon when off,
 * inline `[✎] Edit` button when on. It opens the Compact VQE popover — the
 * DML edit sections for SELECT/DML statements, or the read-only DDL summary
 * card (§2.9 S13) for the 7 true-DDL statement kinds. DDL's own inline DDL
 * toolbar (structural editing) remains separate and unaffected.
 *
 * Owns the live-classified `cursorEditInfo`, the never-silent `vqe-parse`
 * squiggle, and the Compact VQE popover open state. Behavior carried
 * verbatim from the previous inline implementation.
 */
export function useCompactEditAffordance({
  engineId,
  query,
  isActive,
  editorRef,
  queryRef,
  affordanceStatementRange,
  hasMarkerSyntaxError,
  hasLocalSyntaxError,
}: UseCompactEditAffordanceParams) {
  const vqeParseOptions = useMemo<ParseOptions>(() => {
    const sqlEngine = databaseEngineIdToSqlEngine(engineId);
    return sqlEngine ? { engine: sqlEngine } : { engine: 'postgres' };
  }, [engineId]);
  // Pencil CLASSIFICATION is a live path: which affordance shows (compact pencil
  // vs DDL toolbar) and in what mode must reflect the statement under the caret
  // RIGHT NOW, not the up-to-200ms-stale debounced `query`. A fast
  // type-then-click-pencil would otherwise classify against an old parse
  // snapshot (e.g. show the compact pencil for a SELECT the user just replaced
  // with a CREATE TABLE). `resolveLiveQuery` reads `editorRef.current.getValue()`
  // (Monaco's truth), and `currentCursorStatement` re-renders on every
  // statement-changing keystroke, so this is fresh at each render that matters.
  //
  // IMPORTANT: this live value drives CLASSIFICATION only. The edit-context
  // hosts (CompactVqePopover / the compact DDL edit-context hook) are deliberately fed the
  // debounced `query`, NOT this live value: useInlineToolbarEditContext re-seeds
  // its resolver document via onTextChange + recreates the Monaco model adapter
  // on every `query` change, so feeding an every-render live getValue() re-seeds
  // the document mid-edit and tears down their nested body-portalled Radix
  // pickers (e.g. the WHERE "Add filter" form), detaching controls while the
  // user interacts. The debounce coalesces those re-seeds; the host catches up
  // to the live buffer ~200ms after open via the next onTextChange.
  const liveQuery = resolveLiveQuery(editorRef.current, queryRef.current, query);

  const rawCursorEditInfo = useCursorStatementEditInfo(
    liveQuery,
    affordanceStatementRange,
    vqeParseOptions,
    hasMarkerSyntaxError,
    hasLocalSyntaxError,
  );
  // The pencil is shared: `'compact'` opens the Visual Query Editor and
  // `'ddl-card'` opens the Visual DDL Editor. Both sit behind the one
  // `visual-query-editor` beta flag — with it off the pencil is gone for every
  // statement kind, and with it the never-silent teaching indicator and its
  // `vqe-parse` squiggle below, which exist purely to explain why the visual
  // editor can't take this statement. (The Table Designer workspace, which
  // reuses the DDL editor's components, is not an entry point and stays.)
  const vqeEnabled = useBetaFeature('visual-query-editor');
  const cursorEditInfo = useMemo(
    () =>
      vqeEnabled
        ? rawCursorEditInfo
        : { ...rawCursorEditInfo, target: null, unavailableReason: null, unavailableReasonSource: null },
    [vqeEnabled, rawCursorEditInfo],
  );
  // Never-silent squiggle: paint a red Monaco squiggle (dedicated `vqe-parse`
  // marker owner — no global editor-option change) on a statement that
  // genuinely failed to parse, cleared the moment it parses / the cursor moves.
  // Valid-but-unavailable statements (procedural / mixed set-op) are NOT flagged
  // — the hook only marks the `parse-error` reason. NO DOUBLE SQUIGGLE: when the
  // signal came from an external marker (`unavailableReasonSource === 'lsp-marker'`)
  // the language service / MSSQL pass already painted its own squiggle on this
  // exact span, so `vqe-parse` is suppressed; only a `'local-parse'` (or the rare
  // `'render-mode'` parse-error) drives `vqe-parse`, which is then the sole
  // squiggle on the span.
  useParseErrorMarker(
    editorRef,
    cursorEditInfo.unavailableReasonSource === 'lsp-marker' ? null : cursorEditInfo.unavailableReason,
    affordanceStatementRange,
  );
  const [compactEdit, setCompactEdit] = useState<{ open: boolean; source: CompactVqeSource }>({
    open: false,
    source: 'gutter',
  });
  const openCompactEdit = useCallback((source: CompactVqeSource) => {
    setCompactEdit({ open: true, source });
  }, []);

  // Close the compact VQE popover when this tab is deactivated. Inactive tabs
  // stay mounted (CSS-hidden), so the popover's open state otherwise survives a
  // workspace switch and Radix keeps its portalled content floating over the
  // newly-active workspace.
  useEffect(() => {
    if (!isActive && compactEdit.open) {
      setCompactEdit((prev) => ({ ...prev, open: false }));
    }
  }, [isActive, compactEdit.open]);

  return {
    vqeParseOptions,
    cursorEditInfo,
    compactEdit,
    setCompactEdit,
    openCompactEdit,
  };
}
